// import { datadogRum } from '@datadog/browser-rum';
import { isProduction } from '@application/env';
import type { AnimalSensitivityEnum, AnimalTypeEnum, ProfessionalTypeEnum } from '@gql/graphql';
import type {
  StoreBannerTheme,
  StoreCardItemPromoSource,
  StoreCardItemPromoType,
  StoreBannerSource,
  TranslatableContentSource,
} from '@presentation/components';

const isProd = isProduction();

const fbqTrack = (custom: boolean, event: string, props?: any) => {
  if (!window.fbq) {
    return;
  }
  if (!isProd) return;

  window.fbq(custom ? 'trackCustom' : 'track', event, props);
};

const sendSignupEvent = (method = 'Email', label: string) => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', `sign_up_${label}`, { method });

  fbqTrack(false, 'CompleteRegistration', { content_name: label });
};

const sendProContactEvent = (proType: ProfessionalTypeEnum) => {
  if (!isProd) return;
  fbqTrack(false, 'Contact', { content_name: proType.toLowerCase() });
};

const sendOpenCheckoutModalEvent = (value: number) => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'open_checkout_modal', { value });

  // datadogRum.addAction('open_checkout_modal', {
  //   value,
  // });
};

const sendBeginCheckoutEvent = (value: number) => {
  if (!isProd) return;
  if (window.gtag)
    window.gtag('event', 'begin_checkout', {
      currency: 'EUR',
      value,
      items: [],
    });

  // datadogRum.addAction('begin_checkout', {
  //   value,
  // });

  fbqTrack(false, 'InitiateCheckout', { currency: 'EUR', value });
};

const sendPurchaseEvent = (value: number) => {
  if (!isProd) return;
  if (window.gtag)
    window.gtag('event', 'purchase', {
      currency: 'EUR',
      transaction_id: '',
      value,
      items: [],
    });

  // datadogRum.addAction('purchase', {
  //   value,
  // });

  fbqTrack(false, 'Purchase', { currency: 'EUR', value });
};

const sendApplyEvent = (from: 'litter' | 'breeder') => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', `apply_${from}`);

  fbqTrack(false, 'Lead', { content_category: 'application', content_name: from });
};

const sendApplyProgressEvent = (step: number) => {
  if (!isProd) return;
  if (window.gtag)
    window.gtag('event', 'apply_progress_v2', {
      step,
    });
};

const sendApplyConfirmationEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'apply_confirmation');
  // datadogRum.addAction('apply_confirmation');
  fbqTrack(false, 'SubmitApplication');
};

const sendAcceptedByBreederEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'accepted_by_breeder');
};

const sendRejectedByBreederEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'rejected_by_breeder');
};

const sendPuppyCreatedEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'puppy_created');
};

const sendBookingEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'booking');
  fbqTrack(true, 'Booking');
};

const sendGenerateLeadEvent = (from: 'quiz' | 'newsletter' | 'comparator') => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', `lead_${from}`);
};

const sendGetVetInfosEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'get_vet_infos');
};

const sendGetEducatorInfosEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'get_educator_infos');
};

const sendGetShelterInfosEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'get_shelter_infos');
};

const sendGetGrommerInfosEvent = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'get_groomer_infos');
};

const sendAnimalCreatedEvent = (
  type: AnimalTypeEnum | undefined,
  from: 'booking' | 'adoption' | 'manual',
) => {
  if (!isProd) return;
  if (window.gtag)
    window.gtag('event', `animal_created_${from}`, {
      animal_type: type ? type.toLowerCase() : 'other',
    });
};

const sendOpenRequestCouponModal = (partner: string) => {
  if (!isProd) return;
  if (window.gtag)
    window.gtag('event', 'open_request_coupon_modal', {
      open_request_coupon_modal_partner: partner,
    });
};

const sendSubscribeToDogBreeds = (breedNames: string[]) => {
  if (!isProd) return;
  if (window.gtag)
    window.gtag('event', 'subscribed_to_dog_breed', {
      breedNames,
    });
};

const sendClickOnVetAdviceBanner = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'click_on_vet_advice_banner');
};
const sendClickOnVetAdviceWhatsappButton = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'click_on_vet_advice_whatsapp_button');
};
const sendVetAdviceSuccessThankyouPage = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'vet_advice_success_thank_you_page');
};

const clickOnGroomerWhatsapp = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'click_on_groomer_whatsapp');
};

const confirmClickOnGroomerWhatsapp = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'confirm_click_on_groomer_whatsapp');
};

const clickOnBreederWhatsapp = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'click_on_breeder_whatsapp');
};

const confirmClickOnBreederWhatsapp = () => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'confirm_click_on_breeder_whatsapp');
};

const clickOnBookingPromoCard = (sensitivity: AnimalSensitivityEnum) => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', `promo_booking_${sensitivity}`);
};

const clickOnStore = (source: 'header_navigation' | 'sticky_banner') => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'click_on_store', { source });
};
const clickOnStorePromoCard = (type: StoreCardItemPromoType, source: StoreCardItemPromoSource) => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', `click_on_store_promo_${type}`, { source });
};

const clickOnStoreBanner = (theme: StoreBannerTheme, source: StoreBannerSource) => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'click_on_store_banner', { source, theme });
};

const clickOnTranslate = (source: TranslatableContentSource, fromLang: string, toLang: string) => {
  if (!isProd) return;
  if (window.gtag) window.gtag('event', 'click_on_translate', { source, fromLang, toLang });
};

export const Analytics = {
  sendSignupEvent,
  sendProContactEvent,
  sendOpenCheckoutModalEvent,
  sendBeginCheckoutEvent,
  sendPurchaseEvent,
  sendApplyEvent,
  sendApplyProgressEvent,
  sendApplyConfirmationEvent,
  sendAcceptedByBreederEvent,
  sendRejectedByBreederEvent,
  sendPuppyCreatedEvent,
  sendBookingEvent,
  sendGenerateLeadEvent,
  sendGetVetInfosEvent,
  sendGetEducatorInfosEvent,
  sendGetGrommerInfosEvent,
  sendGetShelterInfosEvent,
  sendAnimalCreatedEvent,
  sendOpenRequestCouponModal,
  sendSubscribeToDogBreeds,
  sendClickOnVetAdviceBanner,
  sendClickOnVetAdviceWhatsappButton,
  sendVetAdviceSuccessThankyouPage,
  clickOnGroomerWhatsapp,
  confirmClickOnGroomerWhatsapp,
  clickOnBreederWhatsapp,
  confirmClickOnBreederWhatsapp,
  clickOnBookingPromoCard,
  clickOnStore,
  clickOnStorePromoCard,
  clickOnStoreBanner,
  clickOnTranslate,
};
